import { useCallback, useEffect, useState } from "react";

/**
 * Loads what an overlay shows for a request (`request`: the URL and anything
 * else that changes the answer; null waits). Each answer is kept with the
 * request it answers, so a new request — the customer signed in, a retry —
 * reads as loading from the moment it's asked, never as the previous
 * answer, and a failure recovers on `retry`. `load` resolving to null is a
 * failure.
 */
export function useRequest<T>(
  request: string | null,
  load: () => Promise<T | null>,
): { data: T | null; failed: boolean; retry: () => void } {
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  const key = request === null ? null : `${request}#${attempt}`;
  const [answer, setAnswer] = useState<{ key: string; data: T | null } | null>(null);

  useEffect(() => {
    if (key === null) return;
    let cancelled = false;
    load()
      .then((data) => {
        if (!cancelled) setAnswer({ key, data });
      })
      .catch(() => {
        if (!cancelled) setAnswer({ key, data: null });
      });
    return () => {
      cancelled = true;
    };
    // `load` is read for the request `key` names; a new closure for the same
    // request asks nothing new.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  if (key === null || answer?.key !== key) return { data: null, failed: false, retry };
  return { data: answer.data, failed: answer.data === null, retry };
}
